import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { HandHeart, Heart, Handshake, Loader2, Send } from "lucide-react";
import { Container, Reveal, Overline, SectionHeading } from "../components/common";
import { PageHero } from "../components/PageHero";
import { DonateSection } from "../components/DonateSection";
import { submitVolunteer, submitPartnership } from "../lib/api";
import { IMAGES } from "../lib/content";

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600 transition";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-sm font-medium text-navy mb-1.5">{label}</span>
    {children}
  </label>
);

const VolunteerForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", interest: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await submitVolunteer(form);
      toast.success(data.message || "Thank you for volunteering!");
      setForm({ name: "", email: "", phone: "", city: "", interest: "", message: "" });
    } catch (err) {
      const backendMessage = err?.response?.data?.detail || err?.response?.data?.message || err?.message;
      toast.error(backendMessage || "The volunteer service is not available right now. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="volunteer-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full Name *">
          <input required className={inputCls} value={form.name} onChange={set("name")} data-testid="volunteer-name" placeholder="Your name" />
        </Field>
        <Field label="Email *">
          <input required type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="volunteer-email" placeholder="you@email.com" />
        </Field>
        <Field label="Phone *">
          <input required className={inputCls} value={form.phone} onChange={set("phone")} data-testid="volunteer-phone" placeholder="Mobile number" />
        </Field>
        <Field label="City / Village">
          <input className={inputCls} value={form.city} onChange={set("city")} data-testid="volunteer-city" placeholder="Where are you based?" />
        </Field>
      </div>
      <Field label="Area of Interest">
        <input className={inputCls} value={form.interest} onChange={set("interest")} data-testid="volunteer-interest" placeholder="Education, health camps, skills, events…" />
      </Field>
      <Field label="Why do you want to volunteer?">
        <textarea rows={4} className={inputCls} value={form.message} onChange={set("message")} data-testid="volunteer-message" placeholder="Tell us a little about yourself" />
      </Field>
      <button
        type="submit"
        disabled={loading}
        data-testid="volunteer-submit"
        className="inline-flex items-center gap-2 rounded-full bg-teal-700 px-7 py-3.5 text-sm font-semibold text-white hover:bg-teal-800 transition-colors disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        Submit Application
      </button>
    </form>
  );
};

const PartnershipForm = () => {
  const [form, setForm] = useState({ organization: "", name: "", email: "", phone: "", partnership_type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await submitPartnership(form);
      toast.success(data.message || "Thank you! We'll reach out shortly.");
      setForm({ organization: "", name: "", email: "", phone: "", partnership_type: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="partnership-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Organization *">
          <input required className={inputCls} value={form.organization} onChange={set("organization")} data-testid="partnership-org" placeholder="Company / NGO name" />
        </Field>
        <Field label="Contact Person *">
          <input required className={inputCls} value={form.name} onChange={set("name")} data-testid="partnership-name" placeholder="Your name" />
        </Field>
        <Field label="Email *">
          <input required type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="partnership-email" placeholder="you@company.com" />
        </Field>
        <Field label="Phone">
          <input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="partnership-phone" placeholder="Contact number" />
        </Field>
      </div>
      <Field label="Type of Partnership">
        <input className={inputCls} value={form.partnership_type} onChange={set("partnership_type")} data-testid="partnership-type" placeholder="CSR funding, in-kind, collaboration…" />
      </Field>
      <Field label="Message *">
        <textarea required rows={4} className={inputCls} value={form.message} onChange={set("message")} data-testid="partnership-message" placeholder="How would you like to collaborate?" />
      </Field>
      <button
        type="submit"
        disabled={loading}
        data-testid="partnership-submit"
        className="inline-flex items-center gap-2 rounded-full bg-coral px-7 py-3.5 text-sm font-semibold text-white hover:bg-coral-700 transition-colors disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Handshake className="h-4 w-4" />}
        Send Inquiry
      </button>
    </form>
  );
};

const VolunteerPanel = () => (
  <section className="py-20 lg:py-28 bg-white" data-testid="volunteer-section">
    <Container>
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <Reveal className="lg:col-span-5">
          <span className="grid place-items-center h-14 w-14 rounded-2xl bg-teal-50 text-teal-700">
            <HandHeart className="h-7 w-7" />
          </span>
          <Overline className="mt-6">Volunteer</Overline>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold text-navy leading-tight">
            Give your time, change a life
          </h2>
          <p className="mt-5 text-slate-600 text-lg leading-relaxed">
            Whether you have a weekend or a skill to share, our field teams welcome you. Join
            education drives, health camps, skill workshops and community events across
            Telangana & Andhra Pradesh, with more opportunities opening up across India.
          </p>
          <div className="mt-8 rounded-3xl overflow-hidden aspect-[4/3] shadow-card">
            <img src={IMAGES.volunteers} alt="Volunteers preparing relief kits" className="h-full w-full object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7">
          <div className="rounded-3xl bg-offwhite border border-slate-100 p-7 sm:p-9 shadow-soft">
            <h3 className="font-display text-xl font-bold text-navy mb-6">Volunteer Application</h3>
            <VolunteerForm />
          </div>
        </Reveal>
      </div>
    </Container>
  </section>
);

const INVOLVEMENT_TABS = [
  { id: "volunteer", label: "Volunteer", icon: HandHeart },
  { id: "donation", label: "Donation", icon: Heart },
];

const InvolvementTabs = () => {
  const { hash } = useLocation();
  const [active, setActive] = useState(hash === "#donate" ? "donation" : "volunteer");

  return (
    <>
      <div className="pt-20 lg:pt-28 pb-8 bg-white flex justify-center" data-testid="get-involved-tabs-bar">
        <div className="inline-flex p-1.5 rounded-full bg-teal-50 border border-teal-100" role="tablist" data-testid="get-involved-tablist">
          {INVOLVEMENT_TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={active === t.id}
              onClick={() => setActive(t.id)}
              data-testid={`get-involved-tab-${t.id}`}
              className={`inline-flex items-center gap-2 rounded-full px-6 sm:px-8 py-3 text-sm font-semibold transition-colors ${
                active === t.id ? "bg-teal-700 text-white" : "text-teal-700 hover:text-teal-900"
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className={active === "volunteer" ? "block" : "hidden"} data-testid="volunteer-panel">
        <VolunteerPanel />
      </div>
      <div className={active === "donation" ? "block" : "hidden"} data-testid="donation-panel">
        <DonateSection />
      </div>
    </>
  );
};

export default function GetInvolved() {
  return (
    <>
      <PageHero
        overline="Get Involved"
        title="Be the helping hand a family remembers forever"
        subtitle="Donate, volunteer or partner with us — every act of kindness ripples across a village."
        image={IMAGES.womanHelping}
      />

      <InvolvementTabs />

      <section className="py-20 lg:py-28 bg-offwhite" data-testid="partnership-section">
        <Container>
          <SectionHeading
            overline="Partner With Us"
            title="Corporate & institutional partnerships"
            subtitle="Multiply your CSR impact with a transparent, 80G & CSR-1 compliant grassroots partner."
            center
          />
          <Reveal>
            <div className="max-w-3xl mx-auto rounded-3xl bg-white border border-slate-100 p-7 sm:p-9 shadow-soft">
              <PartnershipForm />
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
}
